// @/components/reusable/animated-number.jsx

/**
 * AnimatedNumber - Animated counting number component
 *
 * Features:
 * - Fast tween-based counting animation (0.6s default)
 * - Viewport-triggered animation (optional)
 * - External trigger support for parent-controlled animations
 * - Configurable formatting (decimals, suffixes)
 *
 * Usage:
 * <AnimatedNumber value={1234567} />                    // "1234567"
 * <AnimatedNumber value={1234} decimals={0} />          // "1234"
 * <AnimatedNumber value={42.5} suffix="kt" />           // "42.5kt"
 * <AnimatedNumber value={100} format={(v) => `${v}%`} />// "100%"
 * <AnimatedNumber value={100} trigger={isInView} />     // External trigger
 */

import React, { useState, useEffect, useRef } from 'react';
import { useMotionValue, animate, useInView } from 'motion/react';

// ═══════════════════════════════════════════════════════════════════════════════
// Formatters
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Default number formatter with automatic suffix (k, M, B)
 */
export const formatWithSuffix = (num, decimals = 1) => {
  if (num === null || num === undefined || Number.isNaN(num)) return '—';

  const absNum = Math.abs(num);

  if (absNum >= 1_000_000_000) {
    return `${(num / 1_000_000_000).toFixed(decimals)}B`;
  }
  if (absNum >= 1_000_000) {
    return `${(num / 1_000_000).toFixed(decimals)}M`;
  }
  if (absNum >= 1_000) {
    return `${(num / 1_000).toFixed(decimals)}k`;
  }

  return num.toFixed(decimals);
};

/**
 * Simple decimal formatter (no suffix)
 */
export const formatDecimal = (num, decimals = 1) => {
  if (num === null || num === undefined || Number.isNaN(num)) return '—';
  return num.toFixed(decimals);
};

// ═══════════════════════════════════════════════════════════════════════════════
// AnimatedNumber Component
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * AnimatedNumber Component
 *
 * @param {number} value - The target number to animate to
 * @param {number} decimals - Decimal places (default: 1)
 * @param {string} suffix - Optional suffix to append (e.g., "kt", "%")
 * @param {string} prefix - Optional prefix to prepend (e.g., "$", "-")
 * @param {Function} format - Custom format function (receives current value)
 * @param {boolean} animateOnView - Use internal viewport detection (default: true)
 * @param {boolean} trigger - External trigger - when true, starts animation (overrides animateOnView)
 * @param {number} delay - Animation delay in seconds (default: 0)
 * @param {number} duration - Animation duration in seconds (default: 0.6)
 * @param {string} className - Additional CSS classes
 */
const AnimatedNumber = ({
  value,
  decimals = 1,
  suffix = '',
  prefix = '',
  format,
  animateOnView = true,
  trigger,
  delay = 0,
  duration = 0.6,
  className = '',
}) => {
  const ref = useRef(null);
  const motionValue = useMotionValue(0);
  const [displayValue, setDisplayValue] = useState('0');
  const internalIsInView = useInView(ref, { once: true, amount: 0.3 });
  const hasAnimated = useRef(false);

  // Use external trigger if provided, otherwise use internal viewport detection
  const shouldAnimate = trigger !== undefined ? trigger : (animateOnView ? internalIsInView : true);

  // Determine the formatter to use
  const formatter = format || ((v) => v.toFixed(decimals));

  useEffect(() => {
    // Don't animate until triggered
    if (!shouldAnimate) {
      return;
    }

    // Start animation - use fast tween, not slow spring
    const controls = animate(motionValue, value, {
      duration,
      delay,
      ease: [0.4, 0, 0.2, 1], // ease-out
      onUpdate: (latest) => {
        setDisplayValue(formatter(latest));
      },
      onComplete: () => {
        setDisplayValue(formatter(value));
      },
    });

    hasAnimated.current = true;

    return () => controls.stop();
  }, [value, shouldAnimate, delay, duration, formatter, motionValue]);

  // Show initial value as 0 formatted
  useEffect(() => {
    if (!hasAnimated.current) {
      setDisplayValue(formatter(0));
    }
  }, [formatter]);

  return (
    <span ref={ref} className={className}>
      {prefix}{displayValue}{suffix}
    </span>
  );
};

export default AnimatedNumber;
